import React, { useState, useEffect, useCallback } from "react";
import {
  Card, Row, Col, Spin, Empty, Select, DatePicker, Typography, Table, Tag,
  Tooltip as AntTooltip, message, Modal, Image, Space, Alert, Descriptions, Button, Segmented, Input, Popconfirm,
} from "antd";
import { PictureOutlined, TrophyOutlined, CheckCircleOutlined, FileExcelOutlined, UserOutlined, WarningOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import {
  getComplianceSelections, getComplianceBatch, getComplianceNoSelection, getComplianceReview,
  getComplianceDailyReport, getCameras, selectBatchFrame,
} from "../network/api";
import { useAuth } from "../context/AuthContext";
import InfoHint from "./common/InfoHint";
import SelectionTuning from "./selectionTuning";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const GOOD = "#52c41a";
const MANUAL = "#722ed1";
// Manual override (O109): a reviewer may rescue any batch, but rescuing one older than this many
// hours warns first — a stale shelf state pushed into today's compliance can misreport reality.
// A warn, not a block; the reviewer confirms through it.
const MANUAL_OVERRIDE_STALE_HOURS = 24;

const pct = (x) => (x == null ? "—" : `${Math.round(x * 100)}%`);
const chillerColor = (c) => (c == null ? "default" : c >= 0.75 ? "green" : c >= 0.4 ? "orange" : "red");
const shortId = (id) => (id ? String(id).slice(-6) : "—");
const num2 = (x) => (x == null ? "—" : Number(x).toFixed(2));

// Compact mask-completeness readout for a frame: how much of the frame the chiller OUTLINE
// fills, how solid it is, and whether it runs off any edge. "no mask" is a real, distinct state
// (the model returned no outline) — the fail-closed selection gates reject those, so it is shown
// rather than blanked. Used in both the selected-frames table and the per-batch candidate list.
const framingCell = (r) => {
  if (r.chillerMaskPresent !== true) return <Tag color="default">no mask</Tag>;
  const cut = Array.isArray(r.chillerCutOffSides) ? r.chillerCutOffSides : [];
  return (
    <Space size={2} direction="vertical" align="start">
      <Text style={{ fontSize: 11 }}>
        fill {pct(r.chillerFrameFill)} · sol {num2(r.chillerSolidity)}
      </Text>
      {cut.length ? (
        <Tag color="error" style={{ marginInlineEnd: 0 }}>cut off: {cut.join(", ")}</Tag>
      ) : (
        <Tag color="green" style={{ marginInlineEnd: 0 }}>in shot</Tag>
      )}
    </Space>
  );
};

// One row of the per-frame metric list in the batch modal.
const metric = (label, value) => (
  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, lineHeight: 1.5 }}>
    <Text type="secondary" style={{ fontSize: 11 }}>{label}</Text>
    <Text style={{ fontSize: 11 }}>{value}</Text>
  </div>
);

// Full decision breakdown for one candidate frame in the batch modal: the verdict (would it go to
// Planogram), the single check that decided it, and every value those checks read. Prefers the
// verdict/reason PERSISTED at selection time (complianceVerdict / complianceReason) — the true
// record for that run — and only falls back to the chiller reason for frames selected before the
// decision was captured.
const frameDecision = (f) => {
  const cut = Array.isArray(f.chillerCutOffSides) ? f.chillerCutOffSides : [];
  const verdictTag = f.manualOverride ? (
    <Tag color="purple" icon={<UserOutlined />} style={{ marginInlineEnd: 0 }}>manual override</Tag>
  ) : f.isSelected ? (
    <Tag color="green" icon={<TrophyOutlined />} style={{ marginInlineEnd: 0 }}>selected</Tag>
  ) : f.complianceVerdict === "eligible" ? (
    <Tag color="green" style={{ marginInlineEnd: 0 }}>would send</Tag>
  ) : f.complianceVerdict === "blocked" ? (
    <Tag color="red" style={{ marginInlineEnd: 0 }}>blocked</Tag>
  ) : (
    <Tag style={{ marginInlineEnd: 0 }}>no verdict</Tag>
  );
  const reason =
    f.complianceReason ||
    (f.chillerReason && f.chillerReason !== "ok" ? f.chillerReason : null);
  return (
    <div style={{ marginTop: 6 }}>
      <Space size={4} wrap>{verdictTag}</Space>
      {f.manualOverride && f.manualOverrideBy?.name && (
        <div style={{ marginTop: 2 }}>
          <Text type="secondary" style={{ fontSize: 11 }}>
            by {f.manualOverrideBy.name}{f.manualOverrideAt ? ` · ${dayjs(f.manualOverrideAt).format("MM/DD HH:mm")}` : ""}
          </Text>
        </div>
      )}
      {reason && (
        <div style={{ marginTop: 2 }}>
          <Text type="secondary" style={{ fontSize: 11 }}>decided by: </Text>
          <Text style={{ fontSize: 11 }}>{reason}</Text>
        </div>
      )}
      <div style={{ marginTop: 4, borderTop: "1px solid #303030", paddingTop: 4 }}>
        {metric("chiller conf", pct(f.chillerConfidence))}
        {metric("focus", f.focusScore != null ? Math.round(f.focusScore) : "—")}
        {metric("person", f.humanMaxScore != null ? num2(f.humanMaxScore) : "—")}
        {metric("SKUs", f.skuTotal != null ? f.skuTotal : "—")}
        {f.chillerMaskPresent === true ? (
          <>
            {metric("fills", pct(f.chillerFrameFill))}
            {metric("solidity", num2(f.chillerSolidity))}
            {metric("cut off", cut.length ? cut.join(", ") : "in shot")}
          </>
        ) : (
          metric("mask", "none")
        )}
      </div>
    </div>
  );
};

// Day-granular window, same rule as Door Analytics (whole days, inclusive end).
const dayWindow = (range) => ({
  from: range?.[0]?.startOf("day").toISOString(),
  to: range?.[1]?.endOf("day").toISOString(),
});

const ComplianceSelections = () => {
  const [deviceId, setDeviceId] = useState(null);
  // Free-text device-ID filter, kept in sync with the dropdown. Lets an operator paste an exact
  // device ID — including one not in the cameras dropdown — instead of scrolling the list.
  const [deviceIdInput, setDeviceIdInput] = useState("");
  // Batch-number filter: accepts the short last-6 id shown in the UI ("#8f40c3") or a full
  // ObjectId. `batchInput` is the live text; `batch` is the applied value (set on Search).
  const [batchInput, setBatchInput] = useState("");
  const [batch, setBatch] = useState(null);
  const [range, setRange] = useState([dayjs().subtract(7, "day").startOf("day"), dayjs().endOf("day")]);
  // "selected" = runs that picked a winner; "none" = runs that weighed photos but selected nothing.
  const [mode, setMode] = useState("selected");
  const [cameras, setCameras] = useState([]);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(24);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Batch drill-down (the candidates one selection weighed).
  const [batchFor, setBatchFor] = useState(null);   // the selected row whose batch modal is open
  const [batchData, setBatchData] = useState(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchError, setBatchError] = useState(null);
  // Manual override (O109): the pictureId currently being forced (disables the buttons while in
  // flight). Only hardwareAdmin+ can override — the backend enforces it too.
  const [selecting, setSelecting] = useState(null);
  const { isHardwareAdmin } = useAuth();

  useEffect(() => {
    getCameras(1, 1000, {})
      .then((list) => setCameras(Array.isArray(list) ? list : []))
      .catch(() => setCameras([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const fetcher = mode === "review" ? getComplianceReview
        : mode === "none" ? getComplianceNoSelection : getComplianceSelections;
      const res = await fetcher({
        deviceId: deviceId || undefined,
        batch: batch || undefined,
        ...dayWindow(range),
        page,
        limit: pageSize,
      });
      setRows(Array.isArray(res?.data) ? res.data : []);
      setTotal(Number.isFinite(res?.total) ? res.total : 0);
    } catch (err) {
      console.error("Failed to load compliance selections", err);
      message.error("Could not load compliance selections");
      setRows([]); setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [deviceId, batch, range, page, pageSize, mode]);

  useEffect(() => { load(); }, [load]);

  // Excel export: one row per device per day for the active device + date window.
  // Built client-side with SheetJS (already bundled) so no file streams through the API.
  // Columns mirror the request: photos captured, compliance batches run, frames delivered.
  const exportExcel = useCallback(async () => {
    setExporting(true);
    try {
      const win = dayWindow(range);
      const res = await getComplianceDailyReport({ deviceId: deviceId || undefined, ...win });
      const rowsData = Array.isArray(res?.data) ? res.data : [];
      if (!rowsData.length) {
        message.info("No data to export for this device/date range.");
        return;
      }

      const columns = [
        { header: "Device ID", key: "deviceId", width: 22 },
        { header: "Device Title", key: "deviceTitle", width: 24 },
        { header: "Region", key: "region", width: 16 },
        { header: "Distribution", key: "distribution", width: 22 },
        { header: "Shop Code", key: "shopCode", width: 14 },
        { header: "Shop Name", key: "shopName", width: 26 },
        { header: "Shop Address", key: "shopAddress", width: 34 },
        { header: "Date", key: "date", width: 12 },
        { header: "Daily Photos", key: "dailyPhotos", width: 14 },
        { header: "Batches Run", key: "batches", width: 13 },
        { header: "Photos in Batches", key: "photosConsidered", width: 17 },
        { header: "Avg Photos / Batch", key: "avgPhotosPerBatch", width: 18 },
        { header: "Photos Sent for Compliance", key: "sent", width: 26 },
      ];
      const header = columns.map((c) => c.header);
      const body = rowsData.map((r) => [
        r.deviceId ?? "",
        r.deviceTitle ?? "",
        r.region ?? "",
        r.distribution ?? "",
        r.shopCode ?? "",
        r.shopName ?? "",
        r.shopAddress ?? "",
        r.date ?? "",
        r.dailyPhotos ?? 0,
        r.batches ?? 0,
        r.photosConsidered ?? 0,
        r.avgPhotosPerBatch ?? "",
        r.sent ?? 0,
      ]);
      const ws = XLSX.utils.aoa_to_sheet([header, ...body]);
      ws["!cols"] = columns.map((c) => ({ wch: c.width }));
      ws["!autofilter"] = { ref: XLSX.utils.encode_range({ s: { c: 0, r: 0 }, e: { c: columns.length - 1, r: body.length } }) };

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Daily Photos");
      const fromStr = range?.[0]?.format("YYYY-MM-DD") || "";
      const toStr = range?.[1]?.format("YYYY-MM-DD") || "";
      const scope = deviceId ? `_${deviceId}` : "";
      XLSX.writeFile(wb, `compliance-daily${scope}_${fromStr}_${toStr}.xlsx`, { compression: true });
      message.success(`Exported ${rowsData.length} row${rowsData.length === 1 ? "" : "s"}.`);
    } catch (err) {
      console.error("Compliance daily export failed", err);
      message.error(err?.response?.data?.error || "Could not build the Excel export");
    } finally {
      setExporting(false);
    }
  }, [deviceId, range]);

  const openBatch = useCallback(async (row) => {
    if (!row.complianceBatchId) {
      message.info("This selection predates batch tracking — no candidates recorded.");
      return;
    }
    setBatchFor(row);
    setBatchData(null);
    setBatchError(null);
    setBatchLoading(true);
    try {
      setBatchData(await getComplianceBatch(row.complianceBatchId));
    } catch (err) {
      setBatchError(err?.response?.data?.error || err.message || "Could not load the batch");
    } finally {
      setBatchLoading(false);
    }
  }, []);

  // Force one frame from a no-selection batch into compliance (O109). The chosen frame is queued
  // and delivered to PlanoMaster on this device's next forCompliance pull. On success the batch
  // leaves the "No selection" list, so we close the modal and reload.
  const doManualSelect = useCallback(async (frameId) => {
    if (!batchFor?.complianceBatchId) return;
    setSelecting(frameId);
    try {
      await selectBatchFrame(batchFor.complianceBatchId, frameId);
      message.success("Frame forced into compliance — it will be delivered on this device's next pull.");
      setBatchFor(null);
      load();
    } catch (err) {
      message.error(err?.response?.data?.error || "Could not select this frame");
    } finally {
      setSelecting(null);
    }
  }, [batchFor, load]);

  const cols = [
    {
      title: "Delivered", key: "image", width: 96,
      // Show what Planogram ACTUALLY received — the chiller crop when one was delivered, else the
      // full frame. Previewing the uncropped frame made good picks look bad (a shopper cropped off
      // the edge still showed here). Falls back to the full frame for older rows with no crop. O104.
      render: (_, r) => {
        const delivered = r.deliveredCropUrl || r.absolutePath;
        return delivered ? (
          <Space direction="vertical" size={2} align="center">
            <Image src={delivered} alt="delivered master" width={72} height={54} style={{ objectFit: "cover", borderRadius: 4 }} preview={{ src: delivered }} />
            {r.deliveredCropUrl ? (
              <Tag color="green" style={{ fontSize: 10, lineHeight: "14px", margin: 0, padding: "0 4px" }}>crop</Tag>
            ) : (
              <Tag style={{ fontSize: 10, lineHeight: "14px", margin: 0, padding: "0 4px" }}>full</Tag>
            )}
          </Space>
        ) : (
          <div style={{ width: 72, height: 54, display: "flex", alignItems: "center", justifyContent: "center", border: "1px dashed #555", borderRadius: 4 }}>
            <Text type="secondary" style={{ fontSize: 11 }}>no file</Text>
          </div>
        );
      },
    },
    {
      title: "Device", dataIndex: "deviceId", key: "deviceId",
      render: (v, r) => (
        <span>
          <Text code>{v || "—"}</Text>
          {r.deviceTitle ? <div><Text type="secondary" style={{ fontSize: 11 }}>{r.deviceTitle}</Text></div> : null}
        </span>
      ),
    },
    {
      title: <span>Selected at<InfoHint title="When the compliance routine chose this frame (UTC). A 'manual' tag means a reviewer forced it in from a batch that had selected nothing, rather than it passing the gates automatically." /></span>,
      dataIndex: "selectedAt", key: "selectedAt",
      render: (v, r) => (
        <Space direction="vertical" size={2} align="start">
          <Text style={{ fontSize: 12 }}>{v ? dayjs(v).format("MM/DD HH:mm:ss") : "—"}</Text>
          {r.manualOverride && (
            <AntTooltip title={`Manual override${r.manualOverrideBy?.name ? ` by ${r.manualOverrideBy.name}` : ""}${r.manualOverrideAt ? ` at ${dayjs(r.manualOverrideAt).format("YYYY-MM-DD HH:mm")} UTC` : ""}`}>
              <Tag color="purple" icon={<UserOutlined />} style={{ margin: 0 }}>manual</Tag>
            </AntTooltip>
          )}
        </Space>
      ),
    },
    {
      title: <span>Sent<InfoHint title="Whether this frame was actually returned to Planogram in the compliance request (delivered, not just chosen). DMS only selects a frame inside a Planogram forCompliance call, so a delivered frame WAS handed over in that HTTP response. Frames selected before delivery tracking show 'no record'. True ingest-confirmation would need Planogram to acknowledge receipt." /></span>,
      dataIndex: "deliveredAt", key: "deliveredAt",
      render: (v) =>
        v ? (
          <AntTooltip title={`Returned to Planogram at ${dayjs(v).format("YYYY-MM-DD HH:mm:ss")} UTC`}>
            <Tag color="green" icon={<CheckCircleOutlined />}>Sent</Tag>
          </AntTooltip>
        ) : (
          <AntTooltip title="Selected, but no delivery record — this frame predates delivery tracking.">
            <Tag color="default">no record</Tag>
          </AntTooltip>
        ),
    },
    {
      title: <span>Chiller score<InfoHint title="The chiller-detection confidence this frame won the batch on. Higher means a clearer view of the shelf." /></span>,
      dataIndex: "chillerConfidence", key: "chillerConfidence",
      render: (v) => <Tag color={chillerColor(v)}>{pct(v)}</Tag>,
    },
    {
      title: <span>SKUs<InfoHint title="How many SKUs the gateway detected in this frame during selection. When SKU pre-select is on, the best-stocked of the equally-well-framed candidates is chosen. Blank = not scored for SKUs." /></span>,
      dataIndex: "skuTotal", key: "skuTotal",
      render: (v) => (v != null ? <Tag>{v}</Tag> : <Text type="secondary">—</Text>),
    },
    {
      title: <span>Framing<InfoHint title="Mask completeness from the segmentation model: how much of the frame the chiller OUTLINE fills, how solid it is (1.0 = clean, lower = something chopping it), and whether it runs off a frame edge. 'no mask' = the model returned no outline; the fail-closed cut-off / fill / solidity gates reject those." /></span>,
      key: "framing",
      render: (_, r) => framingCell(r),
    },
    {
      title: <span>Batch<InfoHint title="The selection run this image came from — one run weighs several recent frames and keeps the best. Shows the batch id (last 6) and how many frames it considered." /></span>,
      key: "batch",
      render: (_, r) => (
        <Space size={4} direction="vertical">
          <Text code style={{ fontSize: 11 }}>#{shortId(r.complianceBatchId)}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>
            best of {r.complianceBatchSize ?? "—"}
          </Text>
        </Space>
      ),
    },
    {
      title: "Candidates", key: "candidates",
      render: (_, r) =>
        r.complianceBatchId ? (
          <AntTooltip title="See every frame this run weighed, with its chiller score">
            <Button type="link" size="small" icon={<PictureOutlined />} style={{ padding: 0 }} onClick={() => openBatch(r)}>
              View {r.complianceBatchSize ? `(${r.complianceBatchSize})` : ""}
            </Button>
          </AntTooltip>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
  ];

  // Runs that weighed photos but selected nothing — one row per batch.
  const noSelectionCols = [
    {
      title: <span>Run at<InfoHint title="When the compliance routine weighed this batch (UTC)." /></span>,
      dataIndex: "complianceRunAt", key: "complianceRunAt",
      render: (v) => (v ? dayjs(v).format("MM/DD HH:mm:ss") : "—"),
    },
    {
      title: "Device", dataIndex: "deviceId", key: "deviceId",
      render: (v, r) => (
        <span>
          <Text code>{v || "—"}</Text>
          {r.deviceTitle ? <div><Text type="secondary" style={{ fontSize: 11 }}>{r.deviceTitle}</Text></div> : null}
        </span>
      ),
    },
    {
      title: <span>Considered<InfoHint title="How many photos this run weighed before deciding none were usable." /></span>,
      dataIndex: "consideredCount", key: "consideredCount",
      render: (v) => (v ?? "—"),
    },
    {
      title: <span>Best chiller<InfoHint title="The highest chiller-detection confidence in the batch. It still fell below the gate, which is why nothing was selected." /></span>,
      dataIndex: "bestChiller", key: "bestChiller",
      render: (v) => <Tag color={chillerColor(v)}>{pct(v)}</Tag>,
    },
    {
      title: "Batch", key: "batch",
      render: (_, r) => <Text code style={{ fontSize: 11 }}>#{shortId(r.complianceBatchId)}</Text>,
    },
    {
      title: "Rejected photos", key: "rejected",
      render: (_, r) => (
        <AntTooltip title="See the photos this run weighed and rejected, with their chiller scores/reasons">
          <Button type="link" size="small" icon={<PictureOutlined />} style={{ padding: 0 }} onClick={() => openBatch(r)}>
            View {r.consideredCount ? `(${r.consideredCount})` : ""}
          </Button>
        </AntTooltip>
      ),
    },
  ];

  // Borderline person-in-crop calls to audit (O106): each row is a frame whose in-crop person
  // score sits near the exclusion bar — eyeball the image to see if the "person" is real or noise.
  const reviewCols = [
    {
      title: "Image", key: "image", width: 96,
      render: (_, r) => {
        const src = r.deliveredCropUrl || r.absolutePath;
        return src
          ? <Image src={src} alt="frame" width={72} height={54} style={{ objectFit: "cover", borderRadius: 4 }} preview={{ src }} />
          : <Text type="secondary" style={{ fontSize: 11 }}>—</Text>;
      },
    },
    {
      title: "Device", dataIndex: "deviceId", key: "deviceId",
      render: (v, r) => (
        <span><Text code>{v || "—"}</Text>
          {r.deviceTitle ? <div><Text type="secondary" style={{ fontSize: 11 }}>{r.deviceTitle}</Text></div> : null}</span>
      ),
    },
    {
      title: <span>Verdict<InfoHint title="What the crop-first-human gate did: 'held' = the in-crop person score met/exceeded the bar so nothing was sent; 'sent' = it was under the bar and delivered." /></span>,
      dataIndex: "verdict", key: "verdict",
      render: (v) => <Tag color={v === "held" ? "volcano" : "green"}>{v === "held" ? "held" : "sent"}</Tag>,
    },
    {
      title: <span>Person in-crop<InfoHint title="The person score INSIDE the delivered crop. Near-threshold scores are often the detector's low-confidence full-frame phantom box on a clean chiller, not a real person — open the image to check." /></span>,
      dataIndex: "humanInCrop", key: "humanInCrop",
      sorter: (a, b) => (a.humanInCrop ?? 0) - (b.humanInCrop ?? 0),
      render: (v) => (v != null ? <Tag color={v >= 0.3 ? "volcano" : "gold"}>{Number(v).toFixed(2)}</Tag> : "—"),
    },
    {
      title: <span>Person full-frame<InfoHint title="The strongest person score anywhere in the frame (before cropping)." /></span>,
      dataIndex: "humanMaxScore", key: "humanMaxScore",
      render: (v) => (v != null ? Number(v).toFixed(2) : "—"),
    },
    {
      title: <span>Chiller</span>, dataIndex: "chillerConfidence", key: "chillerConfidence",
      render: (v) => <Tag color={chillerColor(v)}>{pct(v)}</Tag>,
    },
    {
      title: "Focus", dataIndex: "focusScore", key: "focusScore",
      render: (v) => (v != null ? Math.round(v) : "—"),
    },
    {
      title: "When", dataIndex: "humanInCropAt", key: "humanInCropAt",
      render: (v) => (v ? dayjs(v).format("MM/DD HH:mm:ss") : "—"),
    },
    {
      title: "Batch", key: "batch",
      render: (_, r) => (
        <AntTooltip title="See all photos this run weighed">
          <Button type="link" size="small" style={{ padding: 0 }} onClick={() => openBatch(r)}>#{shortId(r.complianceBatchId)}</Button>
        </AntTooltip>
      ),
    },
  ];

  const isNone = mode === "none";
  const isReview = mode === "review";

  return (
    <div style={{ padding: 16 }}>
      <Row justify="space-between" align="middle" gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            Compliance Selections
            <InfoHint iconStyle={{ fontSize: 15 }} title="The frames the Planogram compliance routine actually ran on. Each run weighs several recent frames and keeps the best-scored one on chiller detection; this shows which frame won, and lets you open the batch to see the candidates it beat. Filter by device and date above." />
          </Title>
          <Text type="secondary">
            {deviceId ? `Best-image selections · Device ${deviceId}` : "Best-image selections · Fleet"}
          </Text>
        </Col>
        <Col>
          <Row gutter={[8, 8]} align="middle">
            <Col>
              <Segmented
                value={mode}
                onChange={(v) => { setMode(v); setPage(1); }}
                options={[
                  { label: "Selected", value: "selected" },
                  { label: "No selection", value: "none" },
                  { label: "Review", value: "review" },
                  { label: "Tuning", value: "tuning" },
                ]}
              />
            </Col>
            <Col>
              <Input.Search
                allowClear
                placeholder="Device ID"
                style={{ width: 150 }}
                value={deviceIdInput}
                onChange={(e) => {
                  const val = e.target.value;
                  setDeviceIdInput(val);
                  // Clearing the box (the × or emptying it) drops the filter immediately.
                  if (val.trim() === "") { setDeviceId(null); setPage(1); }
                }}
                onSearch={(val) => { setDeviceId(val.trim() || null); setPage(1); }}
              />
            </Col>
            <Col>
              <AntTooltip title="Find a specific batch by its number — paste the short id shown in the table (e.g. #8f40c3) or a full batch id.">
                <Input.Search
                  allowClear
                  placeholder="Batch #"
                  style={{ width: 140 }}
                  value={batchInput}
                  onChange={(e) => {
                    const val = e.target.value;
                    setBatchInput(val);
                    // Clearing the box drops the filter immediately.
                    if (val.trim() === "") { setBatch(null); setPage(1); }
                  }}
                  onSearch={(val) => { setBatch(val.trim().replace(/^#/, "") || null); setPage(1); }}
                />
              </AntTooltip>
            </Col>
            <Col>
              <Select
                allowClear showSearch placeholder="All devices" style={{ width: 210 }}
                value={deviceId}
                onChange={(v) => { setDeviceId(v || null); setDeviceIdInput(v || ""); setPage(1); }}
                optionFilterProp="label"
                options={cameras.map((c) => ({
                  value: c.deviceId,
                  label: `${c.deviceId}${c?.config?.title ? ` · ${c.config.title}` : ""}`,
                }))}
              />
            </Col>
            <Col>
              <RangePicker
                allowClear={false}
                value={range}
                onChange={(v) => { if (v) { setRange(v); setPage(1); } }}
                presets={[
                  { label: "Today", value: [dayjs().startOf("day"), dayjs().endOf("day")] },
                  { label: "7d", value: [dayjs().subtract(7, "day").startOf("day"), dayjs().endOf("day")] },
                  { label: "30d", value: [dayjs().subtract(30, "day").startOf("day"), dayjs().endOf("day")] },
                ]}
              />
            </Col>
            <Col>
              <AntTooltip title="Download an Excel sheet — one row per device per day for the current device + date filters — with daily photos captured, compliance batches run, and photos sent for compliance (delivered to Planogram). Days are counted in Pakistan local time.">
                <Button icon={<FileExcelOutlined />} loading={exporting} onClick={exportExcel}>
                  Export Excel
                </Button>
              </AntTooltip>
            </Col>
          </Row>
        </Col>
      </Row>

      {mode === "tuning" ? (
        <SelectionTuning initialDeviceId={deviceId} initialBatch={batch} cameras={cameras} />
      ) : (
      <Card
        title={
          isReview
            ? <span>Borderline person calls<InfoHint title="Frames whose in-crop person score sits near the exclusion bar (0.15–0.45). Open the image to see whether the 'person' is real or the detector's low-confidence full-frame phantom box on a clean chiller — this is where the humanExclusionScore threshold gets tuned." /></span>
            : isNone
            ? <span>Runs that selected nothing<InfoHint title="Compliance runs that weighed photos but chose no winner — nothing cleared the chiller gate. Open a row to review the rejected photos." /></span>
            : <span>Selected images<InfoHint title="One row per frame compliance ran on, newest first. The Batch column ties each back to the run it was chosen from." /></span>
        }
        extra={<Text type="secondary" style={{ fontSize: 12 }}>{total.toLocaleString()} in range</Text>}
      >
        <Spin spinning={loading}>
          <Table
            size="small"
            rowKey={(r) => r._id || r.complianceBatchId}
            columns={isReview ? reviewCols : isNone ? noSelectionCols : cols}
            dataSource={rows}
            pagination={{
              current: page, pageSize, total,
              showSizeChanger: true, pageSizeOptions: [12, 24, 48, 96],
              showTotal: (t, [a, b]) => `${a}-${b} of ${t.toLocaleString()}`,
              onChange: (p, s) => { setPage(p); setPageSize(s); },
            }}
            locale={{ emptyText: <Empty description={isReview ? "No borderline person calls in this range." : isNone ? "No empty-handed runs in this range — every run selected something." : "No compliance selections in this range."} /> }}
          />
        </Spin>
      </Card>
      )}

      <Modal
        open={Boolean(batchFor)}
        onCancel={() => setBatchFor(null)}
        footer={null}
        width={980}
        title={
          batchFor
            ? `Batch #${shortId(batchFor.complianceBatchId)} · ${batchFor.deviceId || ""} · ` +
              (batchFor.complianceRunAt ? dayjs(batchFor.complianceRunAt).format("MM/DD HH:mm:ss") : "")
            : "Batch"
        }
      >
        {batchLoading && <div style={{ textAlign: "center", padding: 40 }}><Spin /></div>}
        {batchError && <Alert type="error" showIcon message={batchError} />}
        {!batchLoading && !batchError && batchData && (
          <>
            <Descriptions size="small" column={3} bordered style={{ marginBottom: 14 }}>
              <Descriptions.Item label="Frames considered">{batchData.total ?? "—"}</Descriptions.Item>
              <Descriptions.Item label="Run at">
                {batchData.runAt ? dayjs(batchData.runAt).format("YYYY-MM-DD HH:mm:ss") : "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Winner score">
                {(() => {
                  const w = (batchData.data || []).find((f) => f.isSelected);
                  return w ? pct(w.chillerConfidence) : "—";
                })()}
              </Descriptions.Item>
            </Descriptions>

            {(() => {
              // Manual override affordance (O109): only on a batch that selected nothing, and only
              // for hardwareAdmin+. `hasSelection === false` is the server's word that the batch is
              // still overridable.
              const overridable = batchData.hasSelection === false && isHardwareAdmin;
              if (!overridable) {
                if (batchData.hasSelection === false && !isHardwareAdmin) {
                  return (
                    <Alert
                      type="info" showIcon style={{ marginBottom: 12 }}
                      message="This batch selected nothing. A Hardware Admin can force one of these frames into compliance."
                    />
                  );
                }
                return null;
              }
              const stale =
                batchFor?.complianceRunAt &&
                dayjs().diff(dayjs(batchFor.complianceRunAt), "hour") >= MANUAL_OVERRIDE_STALE_HOURS;
              return (
                <Alert
                  type={stale ? "warning" : "info"}
                  showIcon
                  style={{ marginBottom: 12 }}
                  message={
                    stale
                      ? "This batch is over 24h old"
                      : "Nothing cleared the gates for this batch"
                  }
                  description={
                    stale
                      ? "Forcing a stale frame reports an old shelf state as current compliance. It will be delivered on the device's next pull. Confirm on the frame only if that's intended."
                      : "Pick the best frame and force it through — it becomes the delivered master and is sent to compliance on the device's next pull, recorded as a manual override under your name."
                  }
                />
              );
            })()}

            <Image.PreviewGroup>
              <Row gutter={[12, 12]}>
                {(batchData.data || []).map((f) => (
                  <Col key={f._id} xs={12} sm={8} md={6}>
                    <div style={{ position: "relative", border: f.isSelected ? `2px solid ${GOOD}` : "1px solid #303030", borderRadius: 6, padding: 4 }}>
                      {(() => {
                        // The winner previews the DELIVERED crop (what Planogram got); the other
                        // candidates are the raw frames it chose among. O104.
                        const src = (f.isSelected && f.deliveredCropUrl) ? f.deliveredCropUrl : f.absolutePath;
                        return src ? (
                          <Image src={src} alt="candidate" style={{ borderRadius: 4, width: "100%" }} />
                        ) : (
                          <div style={{ height: 110, display: "flex", alignItems: "center", justifyContent: "center", border: "1px dashed #555", borderRadius: 4 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>no file</Text>
                          </div>
                        );
                      })()}
                      {f.isSelected && f.deliveredCropUrl && (
                        <Tag color="green" style={{ position: "absolute", top: 6, left: 6, fontSize: 10, margin: 0 }}>delivered crop</Tag>
                      )}
                      {frameDecision(f)}
                      {batchData.hasSelection === false && isHardwareAdmin && (() => {
                        const stale =
                          batchFor?.complianceRunAt &&
                          dayjs().diff(dayjs(batchFor.complianceRunAt), "hour") >= MANUAL_OVERRIDE_STALE_HOURS;
                        return (
                          <Popconfirm
                            title="Force this frame into compliance?"
                            description={
                              <div style={{ maxWidth: 260 }}>
                                {stale && (
                                  <div style={{ color: "#d48806", marginBottom: 4 }}>
                                    <WarningOutlined /> This batch is over 24h old — it will report as today's compliance.
                                  </div>
                                )}
                                It becomes the delivered master and is sent on the device's next pull, recorded as a manual override under your name. A batch can only be overridden once.
                              </div>
                            }
                            okText="Force & send"
                            okButtonProps={{ danger: stale, loading: selecting === f._id }}
                            cancelText="Cancel"
                            onConfirm={() => doManualSelect(f._id)}
                          >
                            <Button
                              type="primary"
                              size="small"
                              block
                              icon={<CheckCircleOutlined />}
                              style={{ marginTop: 8, background: MANUAL, borderColor: MANUAL }}
                              loading={selecting === f._id}
                              disabled={selecting != null}
                            >
                              Select &amp; send
                            </Button>
                          </Popconfirm>
                        );
                      })()}
                    </div>
                  </Col>
                ))}
              </Row>
            </Image.PreviewGroup>
          </>
        )}
      </Modal>
    </div>
  );
};

export default ComplianceSelections;
